import React, { ChangeEventHandler, FunctionComponent, useCallback, useMemo, useState } from "react";
import { Box, Grid } from "@mui/material";
import { noop } from "lodash";
import { useTheme } from "@mui/styles";
import { Chip, RoundedIcon } from "@shared/components";
import SelfServiceButton from "@myProfile/components/SelfServiceButton";
import { requestContactUpdateSelector } from "@requestContactUpdate/store/selectors";
import { updateCommonRequestData } from "@requestContactUpdate/store/ducks";
import FileUtils from "@shared/utils/FileUtils";
import useAttachmentValidator from "@requestContactUpdate/hooks/useAttacmentValidator";
import { MimeType } from "@shared/models/mimeType";
import { openNotificationThunk } from "@store/Notification/thunks";
import { isValidEmail, isValidName } from "@requestContactUpdate/utils/validation";
import { useAppDispatch, useAppSelector, useMobileLayout } from "@app/hooks";
import { impersonationStateSelector } from "@impersonateUser/store/selectors";
import { ReactComponent as Clip } from "@assets/svg/clip.svg";
import { ReactComponent as ExcelIcon } from "@assets/svg/file_excel.svg";
import { ReactComponent as ImageIcon } from "@assets/svg/file_image.svg";
import { ReactComponent as PdfIcon } from "@assets/svg/file_pdf.svg";
import { ReactComponent as PowerpointIcon } from "@assets/svg/file_powerpoint.svg";
import { ReactComponent as WordIcon } from "@assets/svg/file_word.svg";
import { ReactComponent as DocumentIcon } from "@assets/svg/file_document.svg";
import { ReactComponent as ExclamationIcon } from "@assets/svg/exclamation.svg";

const MAX_ATTACHMENTS = 4;

interface Props {
  loading: boolean;
  onSubmit(): void;
}

const getAttachmentIcon = (type: MimeType) => {
  switch (type) {
    case MimeType.JPEG:
    case MimeType.PNG:
      return ImageIcon;
    case MimeType.PDF:
      return PdfIcon;
    case MimeType.DOCX:
      return WordIcon;
    case MimeType.XLSX:
      return ExcelIcon;
    case MimeType.PPTX:
      return PowerpointIcon;
    default:
      return DocumentIcon;
  }
};

const RequestContactUpdateControls: FunctionComponent<Props> = ({ onSubmit, loading }) => {
  const {
    validationWarningShown,
    commonFields: { attachments, contactName, contactEmail, message },
  } = useAppSelector(requestContactUpdateSelector);
  const { isImpersonating } = useAppSelector(impersonationStateSelector);

  const dispatch = useAppDispatch();
  const [attachmentProcessing, setAttachmentProcessing] = useState(false);

  const { validateAttachment, allowedExtensions } = useAttachmentValidator();

  const accept = useMemo(() => allowedExtensions.map((ext) => `.${ext}`).join(", "), [allowedExtensions]);

  const currentTotalBytes = useMemo(() => attachments.reduce((sum, a) => sum + a.size, 0), [attachments]);

  const handleAttachFile = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      if (!event.target.files) return;

      const file = event.target.files[0];

      if (!file || !validateAttachment(file, currentTotalBytes)) return;

      setAttachmentProcessing(true);

      // LPC-10865: try/catch/finally guarantees attachmentProcessing resets even
      // if FileReader rejects, otherwise Submit + Attach stay locked forever.
      try {
        const base64 = await FileUtils.toBase64String(file);

        dispatch(
          updateCommonRequestData({
            attachments: [
              ...attachments,
              {
                base64,
                name: file.name,
                type: file.type as MimeType,
                size: file.size,
              },
            ],
          })
        );
      } catch {
        dispatch(
          openNotificationThunk({
            variant: "warning",
            text: "Attachment failed. Please try again.",
          })
        );
      } finally {
        setAttachmentProcessing(false);
      }
    },
    [dispatch, validateAttachment, attachments, currentTotalBytes]
  );

  const handleRemoveAttachment = useCallback(
    (index: number) => {
      dispatch(
        updateCommonRequestData({
          attachments: attachments.filter((_, i) => i !== index),
        })
      );
    },
    [dispatch, attachments]
  );

  const theme = useTheme();
  const isMobile = useMobileLayout();

  const isFormValid = useMemo(() => {
    const hasContactName = contactName && contactName.trim().length > 0 && isValidName(contactName);
    const hasValidEmail = contactEmail && isValidEmail(contactEmail);
    const hasMessage = message && message.trim().length > 0;

    return hasContactName && hasValidEmail && hasMessage;
  }, [contactName, contactEmail, message]);

  const validationErrorMessage = useMemo(() => {
    if (!validationWarningShown) return null;

    return "Please finish filling out information";
  }, [validationWarningShown]);

  return (
    <Box sx={{ width: "100%" }}>
      {attachments.length > 0 && (
        <Box pl={5} pb={isMobile ? 4 : undefined}>
          {attachments.map((attachment, index) => (
            <Box
              key={`${attachment.name}-${index}`}
              sx={{
                mb: index < attachments.length - 1 ? "10px" : 0,
                "& [data-testid^='chip-label-']": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  textOverflow: "unset",
                  overflow: "visible",
                },
              }}
            >
              <Chip
                Icon={getAttachmentIcon(attachment.type)}
                label={attachment.name}
                fullWidth={isMobile}
                onDelete={() => handleRemoveAttachment(index)}
              />
            </Box>
          ))}
          <Box fontSize={14} mt={2.5}>
            Up to four documents may be attached; max total file size of 25MB
          </Box>
        </Box>
      )}
      <Grid
        item
        container
        sx={{
          flexDirection: isMobile ? "column" : "row",
          justifyContent: isMobile ? "center" : "flex-start",
          width: isMobile ? "auto" : "100%",
        }}
        data-testid="request-contact-update-controls"
      >
        <Grid
          item
          container
          xs={isMobile ? undefined : 9}
          sx={{
            flexDirection: isMobile ? "column-reverse" : "row",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Grid item>
            <SelfServiceButton
              upload
              fullWidth
              size={isMobile ? "small" : "medium"}
              onClick={noop}
              shape="rounded"
              startIcon={<Clip />}
              disabled={loading || attachmentProcessing || attachments.length >= MAX_ATTACHMENTS}
            >
              Attach a document
              <input
                type="file"
                id="attachment"
                name="attachment"
                hidden
                accept={accept}
                value=""
                onChange={handleAttachFile}
              />
            </SelfServiceButton>
          </Grid>
          {validationWarningShown && validationErrorMessage && (
            <Grid
              item
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingBottom: isMobile ? 6 : "unset",
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                color="warning.main"
              >
                <Box fontSize={15} fontWeight="500" display="flex" alignItems="center">
                  <RoundedIcon Icon={ExclamationIcon} variant="warning" />
                  <Box ml={2}>ERROR</Box>
                </Box>
                <Box fontSize={18} mt={1}>
                  {validationErrorMessage}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid
          item
          sx={{
            paddingTop: isMobile ? theme.spacing(4) : "unset",
            marginLeft: isMobile ? 0 : "auto",
            alignSelf: isMobile ? "center" : undefined,
          }}
        >
          <SelfServiceButton
            size={isMobile ? "small" : "medium"}
            onClick={onSubmit}
            shape="rounded"
            disabled={loading || attachmentProcessing || !isFormValid || isImpersonating}
            sx={isMobile ? undefined : { minWidth: "unset" }}
          >
            Submit
          </SelfServiceButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RequestContactUpdateControls;
